"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LocalDataService = void 0;
const superstruct_1 = require("superstruct");
const Common_1 = require("./Common");
const Utils_1 = require("./Utils");
const UserConsentTypes_1 = require("./types/UserConsentTypes");
const STORAGE_PREFIX = "ADP";
const IS_NAMED_USER = "is_named_user";
const UNNAMED_ID = "anon_id";
const UNNAMED_CONSENT_PREFS = "anon_consent_prefs";
const UNNAMED_CONSENT_COPY = "anon_consent_copy";
const SESSION_ID = "session_id";
const SESSION_LAST_ACTIVITY = "session_t";
const SESSION_MAX_AGE = "session_ttl";
const SESSION_START_AT = "session_sat";
class LocalDataService {
    constructor(contextService) {
        this.contextService = contextService;
        this.productHashMemo = {};
        this.genKey = (key) => `${STORAGE_PREFIX}:${this.productKey}:${key}`;
        this.getSdkItem = (key) => localStorage.getItem(this.genKey(key));
        this.setSdkItem = (key, value) => localStorage.setItem(this.genKey(key), value);
        this.removeSdkItem = (...keys) => keys.forEach((key) => localStorage.removeItem(this.genKey(key)));
    }
    get productKey() {
        const { 
        // eslint-disable-next-line @typescript-eslint/naming-convention
        product: { id, name, id_provider }, } = this.contextService.context.facets;
        const key = `${id}::${name}::${id_provider}`;
        if (!this.productHashMemo[key]) {
            this.productHashMemo[key] = (0, Utils_1.stringToMD5)(key);
        }
        return this.productHashMemo[key];
    }
    set unnamedUserId(value) {
        this.setSdkItem(UNNAMED_ID, value);
    }
    get unnamedUserId() {
        var _a;
        return (_a = this.getSdkItem(UNNAMED_ID)) !== null && _a !== void 0 ? _a : "";
    }
    get isNamedUser() {
        return this.getSdkItem(IS_NAMED_USER) === "true";
    }
    set isNamedUser(value) {
        this.setSdkItem(IS_NAMED_USER, String(value));
    }
    get userConsentPreferences() {
        const item = this.getSdkItem(UNNAMED_CONSENT_PREFS);
        if (item == null) {
            return undefined;
        }
        const parsed = JSON.parse(item);
        return (0, superstruct_1.is)(parsed, (0, superstruct_1.array)(UserConsentTypes_1.UserConsentPreferenceStruct)) ? parsed : undefined;
    }
    set userConsentPreferences(preferences) {
        if (preferences == null) {
            this.removeSdkItem(UNNAMED_CONSENT_PREFS);
        }
        else {
            this.setSdkItem(UNNAMED_CONSENT_PREFS, JSON.stringify(preferences));
        }
    }
    get userConsentCopy() {
        const item = this.getSdkItem(UNNAMED_CONSENT_COPY);
        if (item == null) {
            return undefined;
        }
        const parsed = JSON.parse(item);
        return (0, superstruct_1.is)(parsed, UserConsentTypes_1.UserConsentCopyStruct) ? parsed : undefined;
    }
    set userConsentCopy(userConsentCopy) {
        if (userConsentCopy == null) {
            this.removeSdkItem(UNNAMED_CONSENT_COPY);
        }
        else {
            this.setSdkItem(UNNAMED_CONSENT_COPY, JSON.stringify(userConsentCopy));
        }
    }
    get sessionId() {
        return this.getSdkItem(SESSION_ID) || "";
    }
    set sessionId(value) {
        this.setSdkItem(SESSION_ID, value);
    }
    get sessionMaxAgeMS() {
        const record = this.getSdkItem(SESSION_MAX_AGE);
        if (!record)
            return Common_1.DEFAULT_SESSION_TTL;
        return parseInt(record, 10);
    }
    set sessionMaxAgeMS(value) {
        this.setSdkItem(SESSION_MAX_AGE, value.toString());
    }
    get sessionLastActivity() {
        const record = this.getSdkItem(SESSION_LAST_ACTIVITY);
        if (!record)
            return 0;
        return parseInt(record, 10);
    }
    set sessionLastActivity(value) {
        this.setSdkItem(SESSION_LAST_ACTIVITY, value.toString());
    }
    get sessionStartedAt() {
        const record = this.getSdkItem(SESSION_START_AT);
        if (!record)
            return 0;
        return parseInt(record, 10);
    }
    set sessionStartedAt(value) {
        this.setSdkItem(SESSION_START_AT, value.toString());
    }
    deleteSession() {
        this.removeSdkItem(SESSION_ID, SESSION_LAST_ACTIVITY, SESSION_MAX_AGE, SESSION_START_AT);
    }
}
exports.LocalDataService = LocalDataService;
